import ClickableTableCell from 'Components/ClickableTableCell'
import CustomTablePagination from 'Components/CustomTablePagination'
import EnhancedTableHead, { type HeadCell } from 'Components/EnhancedTableHead/EnhancedTableHead'
import NoWrapTableCell from 'Components/NoWrapTableCell'
import StyledTableRow from 'Components/StyledTableRow'
import { WithLoader } from 'Components/WithLoader'
import type {
  Codespace,
  FetchCodespaceListParams
} from 'Services/ControlDataManagement/Features/Codespace/types'
import { ROWS_PER_PAGE_DEFAULT, SORT_ORDER_ASC } from 'Util/constants'
import { datetimeFormatter } from 'Util/formatters'
import { Paper, Table, TableBody, TableContainer } from '@mui/material'
import { t } from 'ttag'

interface CodespaceListProps {
  rows: Codespace[]
  isLoading: boolean
  onRowClick: (id: string) => void
  selectedCodespaceId: string | undefined
  params: FetchCodespaceListParams
  handleRequestSort: (event: React.MouseEvent<unknown>, property: string) => void
  handleChangePage: (event: unknown, newPage: number) => void
  count: number
  page: number
}

export default function CodespaceList({
  rows,
  isLoading,
  onRowClick,
  selectedCodespaceId,
  params,
  handleRequestSort,
  handleChangePage,
  count,
  page
}: Readonly<CodespaceListProps>) {
  const headCells: HeadCell[] = [
    {
      id: 'id',
      numeric: false,
      disablePadding: false,
      label: t`Identifier`,
      sortable: true
    },
    {
      id: 'codespaceId',
      numeric: false,
      disablePadding: false,
      label: t`Codespace identifier`,
      sortable: true
    },
    {
      id: 'description',
      numeric: false,
      disablePadding: false,
      label: t`Description`,
      sortable: false
    },
    {
      id: 'modifiedTimestamp',
      numeric: false,
      disablePadding: false,
      label: t`Modified`,
      sortable: false
    },
    {
      id: 'createdTimestamp',
      numeric: false,
      disablePadding: false,
      label: t`Created`,
      sortable: false
    }
  ]

  const orderPrefix = `${params.sort_order === SORT_ORDER_ASC ? '-' : ''}`
  const orderBy = `${orderPrefix}${params.sort_by}`

  return (
    <WithLoader loading={isLoading} rows={ROWS_PER_PAGE_DEFAULT} height={33}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="small">
            <EnhancedTableHead
              orderBy={orderBy}
              headCells={headCells}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {rows.map(row => {
                return (
                  <StyledTableRow
                    hover
                    tabIndex={-1}
                    key={row.id}
                    selected={selectedCodespaceId === row.codespace_id}
                  >
                    <ClickableTableCell onClick={() => onRowClick(row.codespace_id)}>
                      {row.id}
                    </ClickableTableCell>
                    <ClickableTableCell onClick={() => onRowClick(row.codespace_id)}>
                      {row.codespace_id}
                    </ClickableTableCell>
                    <NoWrapTableCell title={row.description}>{row.description}</NoWrapTableCell>
                    <NoWrapTableCell
                      title={datetimeFormatter(row.modified_timestamp)}
                      component="th"
                      scope="row"
                    >
                      {datetimeFormatter(row.modified_timestamp)}
                    </NoWrapTableCell>
                    <NoWrapTableCell
                      title={datetimeFormatter(row.created_timestamp)}
                      component="th"
                      scope="row"
                    >
                      {datetimeFormatter(row.created_timestamp)}
                    </NoWrapTableCell>
                  </StyledTableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination count={count} page={page} onPageChange={handleChangePage} />
      </Paper>
    </WithLoader>
  )
}
